import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import { Input } from "@arcadeai/design-system/components/ui/atoms/input";
import { ScrollArea } from "@arcadeai/design-system/components/ui/atoms/scroll-area";
import { Separator } from "@arcadeai/design-system/components/ui/atoms/separator";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@arcadeai/design-system/components/ui/atoms/sheet";
import { Textarea } from "@arcadeai/design-system/components/ui/atoms/textarea";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@arcadeai/design-system/rhf";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { useCreateSecret, useUpdateSecret } from "@/app/secrets/api/hooks";
import type { CreateSecret, Secret } from "@/app/secrets/types";
import { createSecretSchema } from "@/app/secrets/types";

function VisibilityToggle({
  visible,
  onToggle,
}: {
  visible: boolean;
  onToggle: () => void;
}) {
  return (
    <Button
      className="h-6 gap-1.5 rounded-md px-2 text-xs"
      onClick={onToggle}
      size="sm"
      type="button"
      variant="ghost"
    >
      {visible ? (
        <>
          <EyeOff className="size-3" />
          Hide
        </>
      ) : (
        <>
          <Eye className="size-3" />
          Show
        </>
      )}
    </Button>
  );
}

const DEFAULT_NEW_SECRET_VALUES: CreateSecret = {
  key: "",
  value: "",
  binding: { type: "project" },
  description: "",
};

type SecretSheetProps = {
  secret?: Secret;
  trigger: React.ReactElement;
  defaultBindingType?: "project" | "tenant";
};

export const SecretSheet = ({
  secret,
  trigger,
  defaultBindingType,
}: SecretSheetProps) => {
  const navigate = useNavigate();
  const { type, newKey } = useSearch({
    from: "/_authenticated/orgs/$organizationId/projects/$projectId/auth/secrets/",
  });
  const [showPassword, setShowPassword] = useState(false);

  // Only auto-open the sheet when type=new AND we're not editing an existing secret
  const [isOpen, setIsOpen] = useState(type === "new" && !secret);

  const isEditing = !!secret;

  const defaultValues = useMemo(() => {
    if (secret) {
      return {
        ...secret,
        value: "",
      };
    }
    return {
      ...DEFAULT_NEW_SECRET_VALUES,
      key: newKey ?? DEFAULT_NEW_SECRET_VALUES.key,
      binding: { type: defaultBindingType ?? "project" },
    };
  }, [secret, newKey, defaultBindingType]);

  const form = useForm<CreateSecret>({
    resolver: zodResolver(createSecretSchema),
    defaultValues,
  });

  const { mutate: createSecret, isPending: isCreating } = useCreateSecret();
  const { mutate: updateSecret, isPending: isUpdating } = useUpdateSecret({
    id: secret?.key ?? "",
    binding: secret?.binding?.type,
  });

  const isLoading = isCreating || isUpdating;

  // Clean the URL when the sheet is closed
  const cleanUrl = useCallback(() => {
    navigate({
      to: ".",
      search: (params: Record<string, unknown>) => ({
        ...params,
        newKey: "",
        type: "list",
      }),
    });
  }, [navigate]);

  const handleSubmit = useCallback(
    (values: CreateSecret) => {
      const onSuccess = () => setIsOpen(false);
      try {
        if (isEditing) {
          updateSecret(values, { onSuccess });
        } else {
          createSecret(values, { onSuccess });
        }
      } catch {
        toast.error("Unable to save secret - please try again");
      }
      cleanUrl();
    },
    [isEditing, cleanUrl, updateSecret, createSecret]
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: Clean only when the sheet is opened
  useEffect(() => {
    if (isOpen) {
      form.reset(defaultValues);
      setShowPassword(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      // Remove the newKey and the 'type=new' query params when the sheet is closed
      if (!open) {
        cleanUrl();
      }
      setIsOpen(open);
    },
    [cleanUrl]
  );

  const handleError = useCallback(() => {
    toast.error("Some required information is missing or incorrect");
  }, []);

  return (
    <Sheet onOpenChange={handleOpenChange} open={isOpen}>
      <SheetTrigger render={trigger} />
      <SheetContent className="w-full px-2">
        <ScrollArea className="h-full w-full">
          <SheetHeader>
            <SheetTitle className="mt-2">
              {isEditing ? "Modify Existing Secret" : "Add New Secret"}
            </SheetTitle>
            <SheetDescription>
              {isEditing
                ? "Update the details of your existing secret."
                : "Fill in the details to create a new secret for your configuration."}
            </SheetDescription>
          </SheetHeader>
          <div className="mt-4 px-4">
            <Form {...form}>
              <form
                className="space-y-6"
                onSubmit={form.handleSubmit(handleSubmit, handleError)}
              >
                {/* Basic Information Section */}
                <div className="space-y-4">
                  <FormField
                    control={form.control}
                    name="key"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>ID</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            autoFocus
                            disabled={isEditing}
                            onChange={(e) => {
                              const value = e.target.value.replace(
                                /[^A-Za-z0-9_-]/g,
                                ""
                              );
                              field.onChange(value);
                            }}
                          />
                        </FormControl>
                        <FormDescription>
                          Unique identifier limited to lowercase, numbers,
                          underscores, and hyphens.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="description"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Description</FormLabel>
                        <FormControl>
                          <Textarea {...field} value={field.value ?? ""} />
                        </FormControl>
                        <FormDescription>
                          Provide a brief description of the secret.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {/* Secret Value Section */}
                <Separator />

                {isEditing ? (
                  <FormField
                    control={form.control}
                    name="value"
                    render={({ field }) => (
                      <FormItem>
                        <div className="flex items-center justify-between">
                          <FormLabel className="flex items-center gap-2">
                            <span>New Secret Value</span>
                            <span className="text-destructive">*</span>
                          </FormLabel>
                          <VisibilityToggle
                            onToggle={() => setShowPassword(!showPassword)}
                            visible={showPassword}
                          />
                        </div>
                        {!!secret && (
                          <div className="flex items-center justify-between rounded border-2 border-amber-200/60 bg-amber-50/30 p-2 dark:border-amber-800/40 dark:bg-amber-950/20">
                            <div className="flex items-center gap-2">
                              <div className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                              <span className="font-medium text-xs">
                                A secret is set
                              </span>
                            </div>
                          </div>
                        )}
                        <FormControl>
                          <Textarea
                            {...field}
                            className={`sensitive break-all ${showPassword ? "" : "[&]:[-webkit-text-security:disc]"} ${
                              form.formState.errors.value
                                ? "border-destructive"
                                : ""
                            }`}
                            placeholder="Enter new secret value"
                            rows={3}
                          />
                        </FormControl>
                        <FormDescription>
                          This will completely replace the current secret value.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                ) : (
                  <FormField
                    control={form.control}
                    name="value"
                    render={({ field }) => (
                      <FormItem>
                        <div className="flex items-center justify-between">
                          <FormLabel className="flex items-center gap-1">
                            Secret Value
                            <span className="text-destructive">*</span>
                          </FormLabel>
                          <VisibilityToggle
                            onToggle={() => setShowPassword(!showPassword)}
                            visible={showPassword}
                          />
                        </div>
                        <FormControl>
                          <Textarea
                            {...field}
                            className={`sensitive break-all ${showPassword ? "" : "[&]:[-webkit-text-security:disc]"} ${
                              form.formState.errors.value
                                ? "border-destructive"
                                : ""
                            }`}
                            placeholder="Enter your secret value"
                            rows={3}
                          />
                        </FormControl>
                        <FormDescription>
                          The secret value will be encrypted and cannot be
                          viewed after saving.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}

                <Button
                  className="mt-4 w-full"
                  disabled={isLoading}
                  type="submit"
                >
                  <p className="flex items-center justify-center gap-3">
                    {!!isLoading && <Loader2 className="size-4 animate-spin" />}
                    <span>{isEditing ? "Update Secret" : "Create Secret"}</span>
                  </p>
                </Button>
              </form>
            </Form>
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
};
